import { post as apiPost, get as apiGet } from './apiService';
import type { AutocompleteResponse, PlaceResponse, Address } from '../types/address';

export interface AutocompleteBias {
  lat?: number;
  lon?: number;
  radius?: number;
}

/**
 * Google Places `types` filter for region-level autocomplete: cities plus
 * neighborhoods, counties, and states. Passed by the locale-search surfaces
 * (`LocaleSearchInput`); address autocomplete omits `types` entirely and gets
 * street addresses. See `isRegionSearch` in the BE's google-maps-api.go —
 * region searches also skip the street-address comma heuristic that would
 * otherwise strip every city prediction.
 */
export const PLACE_TYPES_REGIONS = '(regions)';

export interface AutocompleteOptions {
  sessionToken?: string;
  bias?: AutocompleteBias;
  /**
   * Service key for a logged-out caller (the web `listing_generate` key);
   * authed callers omit it and use their session.
   */
  serviceKey?: string;
  /** Google Places `types` filter, e.g. `PLACE_TYPES_REGIONS`. */
  types?: string;
}

export async function addressAutocomplete(
  searchText: string,
  { sessionToken, bias, serviceKey, types }: AutocompleteOptions = {},
): Promise<AutocompleteResponse> {
  // Bias and types are encoded as query-string params per the backend handler
  // in google-maps-api.go (lat+lon trigger Google Places `Location`/`Radius`
  // bias; otherwise falls back to country restriction).
  const qs = new URLSearchParams();
  if (bias?.lat != null && bias?.lon != null) {
    qs.set('lat', String(bias.lat));
    qs.set('lon', String(bias.lon));
    if (bias.radius != null) qs.set('radius', String(bias.radius));
  }
  if (types) qs.set('types', types);
  const query = qs.toString();
  const url = query ? `/address-search/autocomplete?${query}` : '/address-search/autocomplete';

  return apiPost<AutocompleteResponse>(url, {
    searchText,
    ...(sessionToken ? { sessionToken } : {}),
  }, serviceKey);
}

export async function addressSearch(
  searchText: string,
  stateProvince?: string,
  country?: string,
): Promise<AutocompleteResponse> {
  let url = '/address-search';
  const params = new URLSearchParams();
  if (stateProvince) params.set('stateProvince', stateProvince);
  if (country) params.set('country', country);
  const qs = params.toString();
  if (qs) url += `?${qs}`;
  return apiPost<AutocompleteResponse>(url, { searchText });
}

export async function getPlace(placeId: string): Promise<PlaceResponse> {
  return apiGet<PlaceResponse>(`/place/${placeId}`);
}

export async function geocode(searchText: string): Promise<Address> {
  return apiPost<Address>('/geocode', { searchText });
}

export async function reverseGeocode(lat: number, lon: number): Promise<Address> {
  return apiPost<Address>('/reverse-geocode', { lat, lon });
}
